import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, Package, Euro, SlidersHorizontal, FileText, Plug, UserCog,
  Plus, Pencil, History, Copy, ArrowRight, CheckCircle2, AlertTriangle, Save
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cardCls = 'bg-[#0A0A0A] border border-white/10 rounded-xl';
const inputCls = 'bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30';

function Campo({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
      {children}
    </div>
  );
}

function EstadoBadge({ activo, onLabel = 'Activo', offLabel = 'Inactivo' }) {
  return (
    <span className={`px-2 py-0.5 text-[11px] rounded-full border ${activo
      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
      : 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30'}`}>
      {activo ? onLabel : offLabel}
    </span>
  );
}

// ---------- A) Empresa ----------
function BloqueEmpresa({ empresa, onSaved }) {
  const [form, setForm] = useState(empresa);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(empresa), [empresa]);

  const guardar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/configuracion/empresa`, form, { withCredentials: true });
      toast.success('Datos de empresa guardados. Se aplicarán en PDFs, presupuestos y documentos.');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const campos = [
    ['nombre', 'Nombre comercial'], ['razon_social', 'Razón social'], ['cif', 'CIF'],
    ['direccion', 'Dirección'], ['telefono', 'Teléfono'], ['web', 'Web'], ['email_contacto', 'Email de contacto'],
  ];

  return (
    <div className={`${cardCls} p-4 lg:p-6 space-y-4`} data-testid="config-empresa">
      <p className="text-sm text-zinc-500">Datos corporativos utilizados en PDFs oficiales, presupuestos, documentos y emails.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {campos.map(([key, label]) => (
          <Campo key={key} label={label}>
            <Input value={form?.[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              className={inputCls} data-testid={`empresa-${key}-input`} />
          </Campo>
        ))}
      </div>
      <Button onClick={guardar} disabled={saving} className="bg-white text-black hover:bg-zinc-200" data-testid="empresa-guardar-btn">
        <Save className="w-4 h-4 mr-2" />{saving ? 'Guardando...' : 'Guardar datos de empresa'}
      </Button>
    </div>
  );
}

// ---------- B) Equipos ----------
function EquipoDialog({ equipo, onClose, onSaved }) {
  const esNuevo = !equipo?.id;
  const [form, setForm] = useState({ nombre: equipo?.nombre || '', descripcion: equipo?.descripcion || '', capacidad: equipo?.capacidad || '' });
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    setSaving(true);
    try {
      if (esNuevo) await axios.post(`${API_URL}/api/admin/configuracion/equipos`, form, { withCredentials: true });
      else await axios.put(`${API_URL}/api/admin/configuracion/equipos/${equipo.id}`, form, { withCredentials: true });
      toast.success(esNuevo ? 'Equipo creado' : 'Equipo actualizado');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle>{esNuevo ? 'Nuevo equipo' : `Editar ${equipo.nombre}`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Campo label="Nombre"><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={inputCls} data-testid="equipo-nombre-input" /></Campo>
          <Campo label="Descripción"><Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className={inputCls} rows={2} data-testid="equipo-descripcion-input" /></Campo>
          <Campo label="Capacidad (opcional)"><Input value={form.capacidad || ''} onChange={(e) => setForm({ ...form, capacidad: e.target.value })} placeholder="Ej: 3.5L" className={inputCls} data-testid="equipo-capacidad-input" /></Campo>
          <Button onClick={guardar} disabled={saving || !form.nombre.trim()} className="w-full bg-white text-black hover:bg-zinc-200" data-testid="equipo-guardar-btn">
            {saving ? 'Guardando...' : 'Guardar equipo'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BloqueEquipos({ equipos, onSaved }) {
  const [editando, setEditando] = useState(null);

  const toggleActivo = async (eq) => {
    try {
      await axios.put(`${API_URL}/api/admin/configuracion/equipos/${eq.id}`, { activo: !eq.activo }, { withCredentials: true });
      toast.success(eq.activo ? `${eq.nombre} desactivado: no se ofrecerá en nuevas operaciones (los históricos se conservan)` : `${eq.nombre} activado`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="config-equipos">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-zinc-500">Catálogo operativo. Los equipos desactivados no aparecen en nuevas operaciones pero se conservan en históricos. Con Odoo conectado, Odoo será la fuente de verdad.</p>
        <Button onClick={() => setEditando({})} size="sm" className="bg-white text-black hover:bg-zinc-200 flex-shrink-0" data-testid="equipo-nuevo-btn">
          <Plus className="w-4 h-4 mr-1" />Añadir
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {equipos.map((eq) => (
          <div key={eq.id} className={`${cardCls} p-4 flex items-start justify-between gap-3`} data-testid={`equipo-card-${eq.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">{eq.nombre}</span>
                {eq.capacidad && <span className="text-xs text-zinc-500">{eq.capacidad}</span>}
                <EstadoBadge activo={eq.activo !== false} />
              </div>
              <p className="text-xs text-zinc-500 mt-1">{eq.descripcion}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-white" onClick={() => setEditando(eq)} data-testid={`equipo-editar-${eq.id}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Switch checked={eq.activo !== false} onCheckedChange={() => toggleActivo(eq)} data-testid={`equipo-switch-${eq.id}`} />
            </div>
          </div>
        ))}
      </div>
      {editando && <EquipoDialog equipo={editando.id ? editando : null} onClose={() => setEditando(null)} onSaved={onSaved} />}
    </div>
  );
}

// ---------- C) Tarifas ----------
function TarifaDialog({ tarifa, tipo, equipos, onClose, onSaved }) {
  const esNueva = !tarifa?.id;
  const [form, setForm] = useState({
    nombre: tarifa?.nombre || '', precio: tarifa?.precio ?? '', descripcion: tarifa?.descripcion || '',
    condiciones: tarifa?.condiciones || '', equipo_id: tarifa?.equipo_id || '',
  });
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = { ...form, precio: form.precio === '' ? null : parseFloat(form.precio) };
      if (esNueva) await axios.post(`${API_URL}/api/admin/configuracion/tarifas`, { ...payload, tipo }, { withCredentials: true });
      else await axios.put(`${API_URL}/api/admin/configuracion/tarifas/${tarifa.id}`, payload, { withCredentials: true });
      toast.success(esNueva ? 'Tarifa creada' : 'Tarifa actualizada. Los contratos existentes conservan su precio.');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle>{esNueva ? `Nueva tarifa ${tipo}` : `Editar ${tarifa.nombre}`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Campo label="Nombre"><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={inputCls} data-testid="tarifa-nombre-input" /></Campo>
          <Campo label={`Precio mensual € ${tipo === 'hogar' ? '(IVA incluido)' : '(+IVA)'}`}>
            <Input type="number" step="0.01" value={form.precio} onChange={(e) => setForm({ ...form, precio: e.target.value })}
              placeholder="Vacío = pendiente" className={inputCls} data-testid="tarifa-precio-input" />
          </Campo>
          <Campo label="Equipo asociado">
            <select value={form.equipo_id || ''} onChange={(e) => setForm({ ...form, equipo_id: e.target.value })}
              className="w-full h-10 rounded-md px-3 text-sm bg-black/40 border border-white/10 text-white" data-testid="tarifa-equipo-select">
              <option value="">— Sin equipo específico —</option>
              {equipos.map((eq) => <option key={eq.id} value={eq.id}>{eq.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Descripción"><Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className={inputCls} rows={2} data-testid="tarifa-descripcion-input" /></Campo>
          <Campo label="Condiciones (opcional)"><Textarea value={form.condiciones} onChange={(e) => setForm({ ...form, condiciones: e.target.value })} className={inputCls} rows={2} data-testid="tarifa-condiciones-input" /></Campo>
          <Button onClick={guardar} disabled={saving || !form.nombre.trim()} className="w-full bg-white text-black hover:bg-zinc-200" data-testid="tarifa-guardar-btn">
            {saving ? 'Guardando...' : 'Guardar tarifa'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BloqueTarifas({ tarifas, equipos, onSaved }) {
  const [tipo, setTipo] = useState('hogar');
  const [editando, setEditando] = useState(null);
  const lista = tarifas.filter((t) => t.tipo === tipo);

  const toggleActivo = async (t) => {
    try {
      await axios.put(`${API_URL}/api/admin/configuracion/tarifas/${t.id}`, { activo: !(t.activo !== false) }, { withCredentials: true });
      toast.success('Tarifa actualizada');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="config-tarifas">
      <p className="text-sm text-zinc-500">Las tarifas se usan en presupuestos, formularios y documentos. Modificar una tarifa <b className="text-zinc-300">nunca cambia</b> contratos ni presupuestos ya emitidos (guardan su precio).</p>
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-2">
          {['hogar', 'empresa'].map((t) => (
            <button key={t} onClick={() => setTipo(t)}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${tipo === t ? 'bg-white text-black border-white' : 'text-zinc-400 border-white/10 hover:text-white'}`}
              data-testid={`tarifas-tab-${t}`}>
              {t === 'hogar' ? 'Hogar (IVA incl.)' : 'Empresa (+IVA)'}
            </button>
          ))}
        </div>
        <Button onClick={() => setEditando({})} size="sm" className="bg-white text-black hover:bg-zinc-200 flex-shrink-0" data-testid="tarifa-nueva-btn">
          <Plus className="w-4 h-4 mr-1" />Añadir
        </Button>
      </div>
      <div className="space-y-2">
        {lista.map((t) => (
          <div key={t.id} className={`${cardCls} p-4 flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap`} data-testid={`tarifa-card-${t.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">{t.nombre}</span>
                <EstadoBadge activo={t.activo !== false} onLabel="Activa" offLabel="Inactiva" />
              </div>
              {t.descripcion && <p className="text-xs text-zinc-500 mt-0.5">{t.descripcion}</p>}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
              <span className={`text-sm font-semibold ${t.precio == null ? 'text-amber-400' : 'text-white'}`}>
                {t.precio == null ? 'Pendiente' : `${t.precio.toFixed(2)} €/mes`}
              </span>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-white" onClick={() => setEditando(t)} data-testid={`tarifa-editar-${t.id}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Switch checked={t.activo !== false} onCheckedChange={() => toggleActivo(t)} data-testid={`tarifa-switch-${t.id}`} />
            </div>
          </div>
        ))}
        {lista.length === 0 && <p className="text-sm text-zinc-600 py-6 text-center">No hay tarifas de este tipo todavía.</p>}
      </div>
      {editando && <TarifaDialog tarifa={editando.id ? editando : null} tipo={tipo} equipos={equipos} onClose={() => setEditando(null)} onSaved={onSaved} />}
    </div>
  );
}

// ---------- D) Parámetros de negocio ----------
const DURACIONES_LABELS = {
  prueba: 'Instalación de prueba (min)', instalacion: 'Instalación definitiva (min)',
  mantenimiento: 'Mantenimiento (min)', incidencia: 'Incidencia (min)',
  retirada: 'Retirada (min)', desplazamiento: 'Desplazamiento entre trabajos (min)',
};

function BloqueParametros({ parametros, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm({
      objetivo_mes: parametros?.objetivos_comercial?.objetivo_mes ?? 0,
      objetivo_ano: parametros?.objetivos_comercial?.objetivo_ano ?? 0,
      presupuesto_validez_dias: parametros?.presupuesto_validez_dias ?? 30,
      vacaciones_dias_anuales: parametros?.vacaciones_dias_anuales ?? 22,
      duraciones: parametros?.duraciones_trabajos || {},
    });
  }, [parametros]);

  if (!form) return null;

  const guardar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/configuracion/parametros`, {
        objetivos_comercial: { objetivo_mes: form.objetivo_mes, objetivo_ano: form.objetivo_ano },
        presupuesto_validez_dias: form.presupuesto_validez_dias,
        vacaciones_dias_anuales: form.vacaciones_dias_anuales,
        duraciones_trabajos: form.duraciones,
      }, { withCredentials: true });
      toast.success('Parámetros guardados y aplicados en toda la aplicación');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const num = (v) => (v === '' ? '' : parseInt(v, 10) || 0);

  return (
    <div className="space-y-4" data-testid="config-parametros">
      <div className={`${cardCls} p-4 lg:p-6 space-y-4`}>
        <h3 className="text-white text-sm font-medium">Objetivos comerciales</h3>
        <div className="grid grid-cols-2 gap-4">
          <Campo label="Objetivo mensual (contratos)">
            <Input type="number" value={form.objetivo_mes} onChange={(e) => setForm({ ...form, objetivo_mes: num(e.target.value) })} className={inputCls} data-testid="param-objetivo-mes" />
          </Campo>
          <Campo label="Objetivo anual (contratos)">
            <Input type="number" value={form.objetivo_ano} onChange={(e) => setForm({ ...form, objetivo_ano: num(e.target.value) })} className={inputCls} data-testid="param-objetivo-ano" />
          </Campo>
        </div>
      </div>
      <div className={`${cardCls} p-4 lg:p-6 space-y-4`}>
        <h3 className="text-white text-sm font-medium">Parámetros operativos</h3>
        <div className="grid grid-cols-2 gap-4">
          <Campo label="Validez de presupuestos (días)">
            <Input type="number" value={form.presupuesto_validez_dias} onChange={(e) => setForm({ ...form, presupuesto_validez_dias: num(e.target.value) })} className={inputCls} data-testid="param-validez-presupuestos" />
          </Campo>
          <Campo label="Días de vacaciones anuales">
            <Input type="number" value={form.vacaciones_dias_anuales} onChange={(e) => setForm({ ...form, vacaciones_dias_anuales: num(e.target.value) })} className={inputCls} data-testid="param-vacaciones" />
          </Campo>
        </div>
      </div>
      <div className={`${cardCls} p-4 lg:p-6 space-y-4`}>
        <h3 className="text-white text-sm font-medium">Duraciones estándar de trabajos</h3>
        <p className="text-xs text-zinc-500">Base del futuro Calendario Inteligente (bloques de 15 minutos).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Object.entries(DURACIONES_LABELS).map(([key, label]) => (
            <Campo key={key} label={label}>
              <Input type="number" value={form.duraciones[key] ?? ''} className={inputCls}
                onChange={(e) => setForm({ ...form, duraciones: { ...form.duraciones, [key]: num(e.target.value) } })}
                data-testid={`param-duracion-${key}`} />
            </Campo>
          ))}
        </div>
      </div>
      <Button onClick={guardar} disabled={saving} className="bg-white text-black hover:bg-zinc-200" data-testid="parametros-guardar-btn">
        <Save className="w-4 h-4 mr-2" />{saving ? 'Guardando...' : 'Guardar parámetros'}
      </Button>
    </div>
  );
}

// ---------- E) Plantillas ----------
function PlantillaDialog({ plantilla, roles, onClose, onSaved }) {
  const [form, setForm] = useState({ nombre: plantilla.nombre, descripcion: plantilla.descripcion || '', roles: plantilla.roles || [] });
  const [saving, setSaving] = useState(false);

  const toggleRol = (rol) => setForm({
    ...form, roles: form.roles.includes(rol) ? form.roles.filter((r) => r !== rol) : [...form.roles, rol],
  });

  const guardar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/configuracion/plantillas/${plantilla.codigo}`, form, { withCredentials: true });
      toast.success('Plantilla actualizada');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle>Editar plantilla</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Campo label="Nombre visible"><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={inputCls} data-testid="plantilla-nombre-input" /></Campo>
          <Campo label="Descripción"><Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className={inputCls} rows={2} data-testid="plantilla-descripcion-input" /></Campo>
          <Campo label="Roles autorizados">
            <div className="flex flex-wrap gap-2">
              {roles.map((rol) => (
                <button key={rol} onClick={() => toggleRol(rol)}
                  className={`px-3 py-1.5 rounded-full text-xs border capitalize transition-colors ${form.roles.includes(rol)
                    ? 'bg-white text-black border-white' : 'text-zinc-400 border-white/10 hover:text-white'}`}
                  data-testid={`plantilla-rol-${rol}`}>
                  {rol === 'tecnico' ? 'Técnico' : rol}
                </button>
              ))}
            </div>
          </Campo>
          <Button onClick={guardar} disabled={saving || form.roles.length === 0} className="w-full bg-white text-black hover:bg-zinc-200" data-testid="plantilla-guardar-btn">
            {saving ? 'Guardando...' : 'Guardar plantilla'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BloquePlantillas({ plantillas, roles, onSaved }) {
  const [editando, setEditando] = useState(null);

  const toggleActiva = async (p) => {
    try {
      await axios.put(`${API_URL}/api/admin/configuracion/plantillas/${p.codigo}`, { activa: !p.activa }, { withCredentials: true });
      toast.success(p.activa ? 'Plantilla desactivada: no estará disponible para nuevos documentos' : 'Plantilla activada');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="config-plantillas">
      <div className={`${cardCls} p-4 flex items-start gap-3`}>
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-zinc-400">
          El <b className="text-zinc-200">diseño y contenido oficial</b> de los documentos pertenece a Odoo Firma y no se modifica desde aquí.
          En esta sección solo se gestiona su <b className="text-zinc-200">disponibilidad</b> dentro de la aplicación: nombre visible, descripción, roles autorizados y activación.
          Al conectar Odoo, las plantillas se sincronizarán directamente desde el módulo de Firma.
        </p>
      </div>
      <div className="space-y-2">
        {plantillas.map((p) => (
          <div key={p.codigo} className={`${cardCls} p-4 flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap`} data-testid={`plantilla-card-${p.codigo}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">{p.nombre}</span>
                <EstadoBadge activo={p.activa} onLabel="Disponible" offLabel="Desactivada" />
              </div>
              <p className="text-xs text-zinc-500 mt-0.5">{p.descripcion}</p>
              <div className="flex gap-1.5 mt-1.5 flex-wrap">
                {(p.roles || []).map((r) => (
                  <span key={r} className="px-2 py-0.5 text-[10px] rounded-full bg-white/5 text-zinc-400 border border-white/10 capitalize">
                    {r === 'tecnico' ? 'Técnico' : r}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-white" onClick={() => setEditando(p)} data-testid={`plantilla-editar-${p.codigo}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Switch checked={!!p.activa} onCheckedChange={() => toggleActiva(p)} data-testid={`plantilla-switch-${p.codigo}`} />
            </div>
          </div>
        ))}
      </div>
      {editando && <PlantillaDialog plantilla={editando} roles={roles} onClose={() => setEditando(null)} onSaved={onSaved} />}
    </div>
  );
}

// ---------- Integraciones ----------
function BloqueIntegraciones({ integraciones, onSaved }) {
  const odoo = integraciones?.odoo || {};
  const resend = integraciones?.resend || {};
  const [form, setForm] = useState({ url: odoo.url || '', db: odoo.db || '', username: odoo.username || '', api_key: '' });
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState(null);

  const guardarOdoo = async () => {
    setSaving(true);
    try {
      const payload = { url: form.url, db: form.db, username: form.username };
      if (form.api_key) payload.api_key = form.api_key;
      const r = await axios.put(`${API_URL}/api/admin/configuracion/odoo`, payload, { withCredentials: true });
      toast.success(r.data.odoo.configurado ? 'Conexión con Odoo configurada' : 'Datos guardados (conexión incompleta)');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const cargarEmails = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/configuracion/email-log`, { withCredentials: true });
      setEmails(r.data.emails || []);
    } catch { setEmails([]); }
  };

  const copiar = async (texto) => {
    try { await navigator.clipboard.writeText(texto); toast.success('Enlace copiado'); }
    catch { toast.error('No se pudo copiar'); }
  };

  return (
    <div className="space-y-4" data-testid="config-integraciones">
      {/* Odoo */}
      <div className={`${cardCls} p-4 lg:p-6 space-y-4`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <h3 className="text-white text-sm font-medium">Odoo — Cerebro del negocio</h3>
            <EstadoBadge activo={odoo.configurado} onLabel="Configurado" offLabel="Pendiente" />
          </div>
        </div>
        <p className="text-xs text-zinc-500">
          Con la conexión configurada, la app sincronizará con Odoo: CRM, productos, tarifas, suscripciones,
          plantillas de Firma y emails de negocio (presupuestos, seguimientos, contratos). La app nunca duplica lo que es responsabilidad de Odoo.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Campo label="URL de la instancia"><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://miempresa.odoo.com" className={inputCls} data-testid="odoo-url-input" /></Campo>
          <Campo label="Base de datos"><Input value={form.db} onChange={(e) => setForm({ ...form, db: e.target.value })} className={inputCls} data-testid="odoo-db-input" /></Campo>
          <Campo label="Usuario API"><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className={inputCls} data-testid="odoo-username-input" /></Campo>
          <Campo label={odoo.api_key_definida ? 'API Key (definida — dejar vacío para no cambiar)' : 'API Key'}>
            <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className={inputCls} data-testid="odoo-apikey-input" />
          </Campo>
        </div>
        <Button onClick={guardarOdoo} disabled={saving} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="odoo-guardar-btn">
          {saving ? 'Guardando...' : 'Guardar conexión Odoo'}
        </Button>
      </div>

      {/* Resend */}
      <div className={`${cardCls} p-4 lg:p-6 space-y-3`}>
        <div className="flex items-center gap-2">
          <h3 className="text-white text-sm font-medium">Resend — Emails de autenticación</h3>
          <EstadoBadge activo={resend.configurado} onLabel="Configurado" offLabel="Pendiente de API Key" />
        </div>
        <p className="text-xs text-zinc-500">
          Solo para emails técnicos del Portal: bienvenida, verificación de email y recuperación de contraseña.
          Remitente previsto: <b className="text-zinc-300">{resend.remitente_sistema || 'info@manklar.com'}</b>.
          {!resend.configurado && ' Mientras no haya API Key, los enlaces quedan disponibles abajo y en la ficha de seguridad de cada usuario (/admin/usuarios) para copiarlos manualmente.'}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10" onClick={cargarEmails} data-testid="email-log-btn">
            <History className="w-4 h-4 mr-1" />{emails ? 'Actualizar registro de emails' : 'Ver registro de emails y enlaces de respaldo'}
          </Button>
        </div>
        {emails && (
          <div className="space-y-1.5 max-h-80 overflow-y-auto" data-testid="email-log-list">
            {emails.length === 0 && <p className="text-xs text-zinc-600 py-3">Todavía no hay emails registrados.</p>}
            {emails.map((m, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-black/40 border border-white/5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {m.enviado
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                      : <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
                    <span className="text-xs text-white truncate">{m.to}</span>
                    <span className="text-[10px] text-zinc-500">{m.tipo}</span>
                  </div>
                  <p className="text-[10px] text-zinc-600 truncate">{m.enviado ? 'Enviado' : (m.error || 'No enviado')} · {m.created_at ? new Date(m.created_at).toLocaleString('es-ES') : ''}</p>
                </div>
                {m.enlace && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-white flex-shrink-0" onClick={() => copiar(m.enlace)} data-testid={`email-copiar-${i}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Historial ----------
function HistorialConfig() {
  const [historial, setHistorial] = useState(null);

  const cargar = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/configuracion/historial`, { withCredentials: true });
      setHistorial(r.data.historial || []);
    } catch { setHistorial([]); }
  };

  return (
    <div className={`${cardCls} p-4`} data-testid="config-historial">
      <button onClick={() => (historial ? setHistorial(null) : cargar())} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="historial-toggle-btn">
        <History className="w-4 h-4" />Historial de cambios de configuración
      </button>
      {historial && (
        <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto">
          {historial.length === 0 && <p className="text-xs text-zinc-600">Sin cambios registrados todavía.</p>}
          {historial.map((h) => (
            <div key={h.id} className="px-3 py-2 rounded-lg bg-black/40 border border-white/5">
              <p className="text-xs text-zinc-300">{h.detalle}</p>
              <p className="text-[10px] text-zinc-600 mt-0.5 capitalize">{h.bloque} · {h.usuario} · {h.fecha ? new Date(h.fecha).toLocaleString('es-ES') : ''}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Página ----------
const TABS = [
  { id: 'empresa', label: 'Empresa', icon: Building2 },
  { id: 'equipos', label: 'Equipos', icon: Package },
  { id: 'tarifas', label: 'Tarifas', icon: Euro },
  { id: 'parametros', label: 'Parámetros', icon: SlidersHorizontal },
  { id: 'plantillas', label: 'Plantillas', icon: FileText },
  { id: 'integraciones', label: 'Integraciones', icon: Plug },
];

export default function AdminConfiguracion() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/configuracion`, { withCredentials: true });
      setConfig(r.data);
      setError(false);
    } catch { setError(true); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <AdminLayout title="Configuración" subtitle="Centro de configuración de Manklar — todo sin tocar código" onLogout={handleLogout}>
      <div className="space-y-4 max-w-5xl" data-testid="admin-configuracion-page">
        {/* Acceso directo a usuarios (la gestión vive en /admin/usuarios, no se duplica) */}
        <Link to="/admin/usuarios" data-testid="config-usuarios-link">
          <div className={`${cardCls} p-4 flex items-center justify-between gap-3 hover:border-white/25 transition-colors`}>
            <div className="flex items-center gap-3">
              <UserCog className="w-5 h-5 text-zinc-400" />
              <div>
                <p className="text-sm text-white font-medium">Técnicos y usuarios</p>
                <p className="text-xs text-zinc-500">Crear cuentas, roles, estados (activo/suspendido/desactivado) y seguridad — en Gestión de Usuarios</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          </div>
        </Link>

        {error && (
          <div className={`${cardCls} p-6 text-center`}>
            <p className="text-sm text-zinc-400 mb-3">No se pudo cargar la configuración.</p>
            <Button onClick={fetchConfig} variant="outline" className="border-white/20 text-white" data-testid="config-reintentar-btn">Reintentar</Button>
          </div>
        )}

        {!config && !error && <p className="text-sm text-zinc-500 py-10 text-center">Cargando configuración...</p>}

        {config && (
          <Tabs defaultValue="empresa" className="w-full">
            <TabsList className="w-full h-auto flex justify-start overflow-x-auto bg-[#0A0A0A] border border-white/10 rounded-xl p-1 gap-1">
              {TABS.map(({ id, label, icon: Icon }) => (
                <TabsTrigger key={id} value={id} data-testid={`config-tab-${id}`}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm text-zinc-400 data-[state=active]:bg-white data-[state=active]:text-black rounded-lg whitespace-nowrap flex-shrink-0">
                  <Icon className="w-3.5 h-3.5" />{label}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="empresa" className="mt-4"><BloqueEmpresa empresa={config.empresa} onSaved={fetchConfig} /></TabsContent>
            <TabsContent value="equipos" className="mt-4"><BloqueEquipos equipos={config.equipos} onSaved={fetchConfig} /></TabsContent>
            <TabsContent value="tarifas" className="mt-4"><BloqueTarifas tarifas={config.tarifas} equipos={config.equipos} onSaved={fetchConfig} /></TabsContent>
            <TabsContent value="parametros" className="mt-4"><BloqueParametros parametros={config.parametros} onSaved={fetchConfig} /></TabsContent>
            <TabsContent value="plantillas" className="mt-4"><BloquePlantillas plantillas={config.plantillas} roles={config.roles_plantilla} onSaved={fetchConfig} /></TabsContent>
            <TabsContent value="integraciones" className="mt-4"><BloqueIntegraciones integraciones={config.integraciones} onSaved={fetchConfig} /></TabsContent>
          </Tabs>
        )}

        <HistorialConfig />
      </div>
    </AdminLayout>
  );
}
